import { assert, log } from '../../../app/logger.js';
import { loadScript, loadStyle } from '../../../util/util.js';
import { MapBaseMixin } from '../base/map_base_mixin.js';
import { EventedLeaflet } from './evented_leaflet.js';

const leaflet_version = '1.8.0';
const leaflet_css_integrity =
  'sha512-hoalWLoI8r4UszCkZ5kL8vayOGVae1oxXe/2A4AO6J9+580uKHDO3JdHb7NzwwzK5xr/Fs0W40kiNHxM9vyTtQ==';
const leaflet_js_integrity =
  'sha512-BB3hKbKWOc9Ez/TAwyWxNXeoV9c1v6FIeYiBieIWkpLjauysF18NzgR1MBNBXf8/KABdlkX68nAhlwcDFLGPCQ==';

let g_initalized = false;

/**
 * A wrapper around Leaflet Maps object. Loads leafletjs API.
 */
class MapLeaflet extends MapBaseMixin(EventedLeaflet) {
  static async create(el, options) {
    if (!g_initalized) {
      g_initalized = true;
      loadStyle(
        `https://unpkg.com/leaflet@${leaflet_version}/dist/leaflet.css`,
        leaflet_css_integrity
      );
      await loadScript(
        `https://unpkg.com/leaflet@${leaflet_version}/dist/leaflet.js`,
        leaflet_js_integrity
      );
    }

    return new this(el, options);
  }

  constructor(el, options) {
    super(el, options);
    this.initialize();
  }

  get bounds() {
    let b = this.native.getBounds();
    return (
      b && {
        north: b.getNorthEast().lat,
        east: b.getNorthEast().lng,
        south: b.getSouthWest().lat,
        west: b.getSouthWest().lng,
      }
    );
  }

  fitBounds({ south, east, north, west }) {
    log(
      `[map:leaflet] fitBounds (south: ${south}, east: ${east}, north: ${north}, west: ${west})`
    );
    assert(south, `MapLeaflets.fitBounds: no south`);
    assert(east, `MapLeaflets.fitBounds: no east`);
    assert(north, `MapLeaflets.fitBounds: no north`);
    assert(west, `MapLeaflets.fitBounds: no west`);
    this.native.fitBounds([
      [south, west],
      [north, east],
    ]);
  }

  panTo({ lat, lng, zoom }) {
    log(
      `[map:leaflet] panTo (${lat}, ${lng})${(zoom && `, zoom: ${zoom}`) || ''}`
    );
    this.native.setView({ lat, lng }, zoom || this.zoom);
  }

  panBy(x, y) {
    log(`[map:leaflet] panBy (${x}, ${y})`);
    this.native.panBy({ x, y });
  }

  get center() {
    return {
      lat: this.native.getCenter().lat,
      lng: this.native.getCenter().lng,
    };
  }

  get zoom() {
    return this.native.getZoom();
  }

  set zoom(v) {
    log(`[map:leaflet] zoom to ${v}`);
    this.native.setZoom(v);
  }

  switchMap(type) {
    this.native.setMapTypeId(type);
  }

  drawGrid() {
    throw new Error(`Not implemented`);
  }

  // Protected

  createNative() {
    let native = new window.L.Map(this.el, this.nativeOptions);

    window.L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution:
        '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
      subdomains: ['a', 'b', 'c'],
    }).addTo(native);

    return native;
  }

  static getDefaultNativeOptions() {
    return {
      zoomControl: false,
    };
  }

  static get nativeOptionsMapper() {
    return Object.assign({}, super.nativeOptionsMapper, {
      center: 'center',
      zoom: 'zoom',
    });
  }
}

export { MapLeaflet };
